/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useState, useEffect } from 'react';
import { HelpCircle, Play, BookOpen, MessageCircle, FileText, ChevronRight, Sparkles, Settings, GitBranch, Bot, Wrench, Server, Library } from 'lucide-react';
import { useWalkthrough } from '../contexts/WalkthroughContext';
import { RequestInput } from '../components/common/RequestInput';
import { useRequestProcessor } from '../hooks/useRequestProcessor';
import { SkillShopService } from '../services/skillShopService';
import { useToast } from '../hooks/use-toast';
import { MarkdownRenderer } from '../components/ui/markdown-renderer';

interface HelpDocument {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

// Map document IDs to metadata
const HELP_DOCUMENTS: HelpDocument[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'Introduction to Velocity and spec-driven development',
    icon: <Play size={16} />
  },
  {
    id: 'user-stories',
    title: 'User Stories & Epics',
    description: 'Creating and managing user stories',
    icon: <FileText size={16} />
  },
  {
    id: 'issue-tracking',
    title: 'Issue Tracking',
    description: 'Managing development issues and workflow',
    icon: <GitBranch size={16} />
  },
  {
    id: 'ai-agents',
    title: 'AI Agent Integration',
    description: 'Using AI for grooming and code generation',
    icon: <Sparkles size={16} />
  },
  {
    id: 'agents',
    title: 'Agents',
    description: 'System agents for automated oversight',
    icon: <Bot size={16} />
  },
  {
    id: 'playbooks',
    title: 'Playbooks',
    description: 'Knowledge context for AI agents',
    icon: <Library size={16} />
  },
  {
    id: 'jira-integration',
    title: 'JIRA Integration',
    description: 'Import and export with JIRA',
    icon: <Settings size={16} />
  },
  {
    id: 'linear-integration',
    title: 'Linear Integration',
    description: 'Connect to Linear for issue tracking',
    icon: <Settings size={16} />
  },
  {
    id: 'skills',
    title: 'Skills',
    description: 'Reusable knowledge packages for AI agents',
    icon: <Wrench size={16} />
  },
  {
    id: 'mcp-servers',
    title: 'MCP Servers',
    description: 'External tools and integrations for AI agents',
    icon: <Server size={16} />
  }
];

export function Help() {
  const { state, resetWalkthrough, startWalkthrough } = useWalkthrough();
  const { toast } = useToast();
  const [skillShopService] = useState(() => new SkillShopService());
  
  // Documentation browser state
  const [selectedDoc, setSelectedDoc] = useState<string | null>(null);
  const [docContent, setDocContent] = useState<string>('');
  const [isLoadingDoc, setIsLoadingDoc] = useState(false);

  // Use the generic request processor hook for help questions
  const requestProcessor = useRequestProcessor({
    onProcess: async (request: string) => {
      const result = await skillShopService.processHelpQuestion(request);
      if (result.success && result.result) {
        requestProcessor.setResponse(result.result);
      } else {
        throw new Error(result.error || 'Failed to process help question');
      }
    },
    onSuccess: () => {
      console.log('Help question processed successfully');
    }
  });

  const handleRestartTutorial = () => {
    resetWalkthrough();
    startWalkthrough();
    toast({
      title: 'Tutorial Restarted',
      description: 'The quickstart tutorial has been restarted.',
    });
  };

  const {
    request: questionValue,
    setRequest: setQuestion,
    isProcessing,
    lastResponse,
    isListening,
    speechSupported,
    handleProcess,
    handleKeyPress,
    toggleListening
  } = requestProcessor;

  // Load documentation content
  const loadDocument = async (docId: string) => {
    if (docId === selectedDoc) return;
    
    setIsLoadingDoc(true);
    setSelectedDoc(docId);
    
    try {
      const response = await fetch(`http://localhost:3002/api/help/docs/${docId}`);
      const data = await response.json();
      
      if (data.success && data.data?.content) {
        setDocContent(data.data.content);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load documentation',
          variant: 'destructive'
        });
        setSelectedDoc(null);
      }
    } catch (error) {
      console.error('Failed to load document:', error);
      toast({
        title: 'Error',
        description: 'Failed to load documentation',
        variant: 'destructive'
      });
      setSelectedDoc(null);
    } finally {
      setIsLoadingDoc(false);
    }
  };

  // Back to help home
  const backToHome = () => {
    setSelectedDoc(null);
    setDocContent('');
  };

  // Get current document info
  const currentDoc = HELP_DOCUMENTS.find(d => d.id === selectedDoc);

  // If a document is selected, show the doc browser layout
  if (selectedDoc) {
    return (
      <div style={{ display: 'flex', height: '100%', minHeight: 'calc(100vh - 40px)' }}>
        {/* Left Navigation Panel */}
        <div
          style={{
            width: '280px',
            minWidth: '280px',
            borderRight: '1px solid #e5e7eb',
            backgroundColor: '#f8fafc',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: 'white'
            }}
          >
            <button
              onClick={backToHome}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#0066cc',
                fontSize: '14px',
                fontWeight: 500,
                padding: 0,
                marginBottom: '12px'
              }}
            >
              <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />
              Back to Help
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <BookOpen size={20} style={{ color: '#0066cc' }} />
              <span style={{ fontWeight: 600, fontSize: '16px', color: '#1f2937' }}>
                Documentation
              </span>
            </div>
          </div>

          {/* Navigation Links */}
          <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
            {HELP_DOCUMENTS.map((doc) => (
              <button
                key={doc.id}
                onClick={() => loadDocument(doc.id)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  width: '100%',
                  padding: '12px',
                  border: 'none',
                  borderRadius: '8px',
                  background: selectedDoc === doc.id ? '#e0f2fe' : 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  marginBottom: '4px',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  if (selectedDoc !== doc.id) {
                    e.currentTarget.style.backgroundColor = '#f1f5f9';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedDoc !== doc.id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    backgroundColor: selectedDoc === doc.id ? '#0066cc' : '#e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: selectedDoc === doc.id ? 'white' : '#64748b',
                    flexShrink: 0
                  }}
                >
                  {doc.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: selectedDoc === doc.id ? 600 : 500,
                      fontSize: '14px',
                      color: selectedDoc === doc.id ? '#0066cc' : '#1f2937',
                      marginBottom: '2px'
                    }}
                  >
                    {doc.title}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#64748b',
                      lineHeight: '1.4',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {doc.description}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: 'white'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                backgroundColor: '#fef3c7',
                borderRadius: '8px'
              }}
            >
              <MessageCircle size={16} style={{ color: '#d97706', flexShrink: 0 }} />
              <span style={{ fontSize: '12px', color: '#92400e' }}>
                Need help? Use the AI Agent sidebar for personalized assistance.
              </span>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: 'white'
          }}
        >
          {isLoadingDoc ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px' }}>
              <div style={{ textAlign: 'center', color: '#6b7280' }}>
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    border: '3px solid #e5e7eb',
                    borderTopColor: '#0066cc',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 12px'
                  }}
                />
                Loading documentation...
              </div>
            </div>
          ) : (
            <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px' }}>
              {/* Document Header */}
              {currentDoc && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '8px',
                    paddingBottom: '16px',
                    borderBottom: '1px solid #e5e7eb'
                  }}
                >
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: '#0066cc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}
                  >
                    {currentDoc.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                      Velocity Documentation
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937' }}>
                      {currentDoc.description}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Document Content */}
              <MarkdownRenderer content={docContent} />
            </div>
          )}
        </div>

        {/* CSS Animation */}
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Default help home view
  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
          <HelpCircle style={{ marginRight: '12px', color: '#0066cc' }} />
          <h2 style={{ margin: 0 }}>Help & Support</h2>
        </div>

        {/* Quickstart Tutorial Section */}
        <div
          style={{
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '24px',
            background: '#f0f9ff'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: '#0066cc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <Play size={24} color="white" />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 8px 0', color: '#1e40af' }}>Quickstart Tutorial</h3>
              <p style={{ margin: '0 0 16px 0', color: '#475569', fontSize: '14px' }}>
                {state.hasCompletedWalkthrough
                  ? 'You\'ve completed the quickstart tutorial! You can restart it anytime to refresh your knowledge of Velocity\'s features.'
                  : 'Take a guided tour through Velocity\'s key features. Learn how to manage products, releases, user stories, and issues.'}
              </p>
              <button
                onClick={handleRestartTutorial}
                className="btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Play size={16} />
                {state.hasCompletedWalkthrough ? 'Restart Tutorial' : 'Start Tutorial'}
              </button>
            </div>
          </div>
        </div>

        {/* Ask a Question Section */}
        <RequestInput
          title="Ask the Velocity Guide"
          description="Have a question about Velocity? Ask our AI-powered guide for help with features, workflows, or best practices."
          examples={[
            "How do I create a new product?",
            "What's the difference between issues and user stories?",
            "How do AI agents help with development?",
            "How do I set up JIRA integration?"
          ]}
          value={questionValue}
          isProcessing={isProcessing}
          isListening={isListening}
          lastResponse={lastResponse}
          speechSupported={speechSupported}
          placeholder="Ask a question about Velocity..."
          onValueChange={setQuestion}
          onProcess={handleProcess}
          onVoiceInput={toggleListening}
          onKeyPress={handleKeyPress}
        />

        {/* Documentation Links Section */}
        <div
          style={{
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '24px',
            background: '#f8f9fa'
          }}
        >
          <h3 style={{ margin: '0 0 16px 0', color: '#0066cc', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BookOpen size={20} />
            Documentation & Resources
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
            {HELP_DOCUMENTS.map((doc) => (
              <DocumentationLink
                key={doc.id}
                title={doc.title}
                description={doc.description}
                icon={doc.icon}
                onClick={() => loadDocument(doc.id)}
              />
            ))}
          </div>
        </div>

        {/* Support Section */}
        <div
          style={{
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '20px',
            background: '#fefce8'
          }}
        >
          <h3 style={{ margin: '0 0 16px 0', color: '#854d0e', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MessageCircle size={20} />
            Need More Help?
          </h3>
          <p style={{ margin: '0 0 16px 0', color: '#713f12', fontSize: '14px' }}>
            Can't find what you're looking for? Use the AI Agent sidebar to chat with the Product Manager or Velocity Guide agents for personalized assistance.
          </p>
          <p style={{ margin: 0, color: '#78716c', fontSize: '12px' }}>
            Tip: Click the expand button on the right side of the screen to open the agent sidebar.
          </p>
        </div>
      </div>
    </div>
  );
}

interface DocumentationLinkProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
}

function DocumentationLink({ title, description, icon, onClick }: DocumentationLinkProps) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '14px',
        width: '100%',
        padding: '16px',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        background: 'white',
        textDecoration: 'none',
        textAlign: 'left',
        transition: 'all 0.2s',
        cursor: 'pointer'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#0066cc';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 102, 204, 0.1)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#e5e7eb';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '8px',
          backgroundColor: '#e0f2fe',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#0066cc',
          flexShrink: 0
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px', fontSize: '15px' }}>{title}</div>
        <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.4' }}>{description}</div>
      </div>
      <ChevronRight size={18} style={{ color: '#9ca3af', flexShrink: 0, marginTop: '2px' }} />
    </button>
  );
}
